import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { SendHorizonal, MessageCircle, X } from "lucide-react";

const GeminiChat = () => {

  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const idleTimerRef = useRef(null);

  const BASE_URL = "https://api.circuitcytechnology.com";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ---------------- OUTSIDE CLICK CLOSE ---------------- */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chatRef.current && !chatRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- IDLE CLOSE ---------------- */

  useEffect(() => {
    if (!isOpen) return;

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setIsOpen(false), 30000);
    };

    resetIdleTimer();

    const events = ["keydown", "click"];
    events.forEach((event) => document.addEventListener(event, resetIdleTimer));

    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach((event) =>
        document.removeEventListener(event, resetIdleTimer)
      );
    };
  }, [isOpen]);

  /* ---------------- TYPEWRITER EFFECT ---------------- */

  const typeMessage = (text) => {
    let index = 0;
    let current = "";

    const interval = setInterval(() => {

      current += text[index];
      index++;

      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          from: "ai",
          text: current,
        };
        return updated;
      });

      if (index >= text.length) {
        clearInterval(interval);
      }

    }, 15);
  };

  /* ---------------- SEND MESSAGE ---------------- */

  const sendMessage = async () => {

    if (!userInput.trim()) return;

    const newMessages = [...messages, { from: "user", text: userInput }];

    setMessages(newMessages);
    setUserInput("");
    setLoading(true);

    try {

      const formattedMessages = newMessages.map((m) => ({
        role: m.from === "user" ? "user" : "assistant",
        content: m.text,
      }));

      const response = await axios.post(`${BASE_URL}/ai`, {
        
        messages: formattedMessages,
      });

      const aiReply = response.data.reply;

      const updatedMessages = [...newMessages, { from: "ai", text: "" }];
      setMessages(updatedMessages);

      typeMessage(aiReply);

      /* ---------- SEND TRANSCRIPT EMAIL ---------- */

      if (/email|contact|phone/i.test(aiReply)) {

        const transcript = [...newMessages, { from: "ai", text: aiReply }]
          .map((m) => m.from + ": " + m.text)
          .join("\n");

        await axios.post(`${BASE_URL}/submit-form`, {
          transcript: transcript,
        });

      }

    } catch (err) {

      console.error("AI error:", err.message);

      setMessages([
        ...newMessages,
        { from: "ai", text: "Oops! Something went wrong. Please try again." },
      ]);

    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <>

      {!isOpen && (
        <button
          onClick={() => {

            setIsOpen(true);

            if (messages.length === 0) {
              setMessages([
                { from: "ai", text: "Hi! What course are you pursuing?" },
              ]);
            }

          }}
          className="fixed bottom-6 right-6 bg-sky-600 hover:bg-sky-700 text-white p-4 rounded-full shadow-xl z-50"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {isOpen && (
        <div
          ref={chatRef}
          className="fixed bottom-6 right-6 w-80 sm:w-96 bg-[#0d1117] text-white border border-sky-500 rounded-2xl shadow-2xl z-50 flex flex-col"
        >

          {/* HEADER */}

          <div className="bg-gradient-to-r from-sky-600 via-teal-500 to-sky-400 px-4 py-3 flex justify-between items-center rounded-t-2xl">
            <h2 className="font-semibold">Circuitcy Chat</h2>

            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-300"
            >
              <X size={18} />
            </button>
          </div>

          {/* CHAT BODY */}

          <div className="h-72 overflow-y-auto px-4 py-3 bg-[#161b22] text-sm flex flex-col gap-2">

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`w-full flex ${
                  msg.from === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`px-4 py-2 rounded-xl max-w-[75%] break-words ${
                    msg.from === "user"
                      ? "bg-sky-600 text-white rounded-br-none"
                      : "bg-[#0d1117] text-white border border-sky-500 rounded-bl-none"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start text-sky-300 text-xs italic">
                AI is typing...
              </div>
            )}

            <div ref={messagesEndRef} />

          </div>

          {/* INPUT */}

          <div className="flex items-center bg-[#0d1117] border-t border-gray-700 p-3 gap-2">

            <input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type here..."
              className="flex-1 px-3 py-2 bg-[#161b22] text-white border border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
            />

            <button
              onClick={sendMessage}
              disabled={loading}
              className="bg-sky-500 hover:bg-sky-600 text-white p-2 rounded-full"
            >
              <SendHorizonal size={18} />
            </button>

          </div>

        </div>
      )}
    </>
  );
};

export default GeminiChat;
